import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import { Datagrid, FunctionField, List, TextField, useNotify, useRefresh, useTranslate } from 'react-admin';
import dayjs from 'dayjs';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { baseUrl, httpClient } from '../../../providers/AppDataProvider';
import './init';

const RESULT_COLOR: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  success: 'success',
  failed: 'error',
  pending: 'warning',
  processing: 'warning',
};

const RewardClaimsList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmRecord, setConfirmRecord] = useState<{ id: string; transactionHash?: string; chainId?: number } | null>(null);
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    if (!confirmRecord) return;
    setRetrying(true);
    try {
      await httpClient(`${baseUrl}/agent-reward-claims/${confirmRecord.id}/retry`, { method: 'POST' });
      notify('Retry submitted successfully', { type: 'success' });
      refresh();
    } catch {
      notify('Retry failed', { type: 'error' });
    } finally {
      setRetrying(false);
      setConfirmRecord(null);
    }
  };

  return (
    <DashboardContent>
      <PermissionGuard resource={Resources.rewardClaims} action="read" fallback={<ErrorPage403 />}>
        <CustomBreadcrumbs
          heading={translate('resource.rewardClaims')}
          links={[
            { name: translate('resource.agentRebate') },
            { name: translate('resource.rewardClaims') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List actions={false} empty={false} perPage={25} exporter={false}>
            <Box sx={{ overflowX: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={{
                  '& .RaDatagrid-table': { tableLayout: 'auto', width: 'max-content', minWidth: '100%' },
                  '& .RaDatagrid-thead th, & .RaDatagrid-tbody td': {
                    whiteSpace: 'nowrap',
                    padding: '8px 12px',
                    verticalAlign: 'middle',
                  },
                }}
              >
                <TextField source="userId" label="module.rewardClaims.userId" sortable={false} />
                <TextField source="address" label="module.rewardClaims.address" sortable={false} />
                <TextField source="amount" label="module.rewardClaims.amount" sortable={false} />
                <TextField source="amountUsd" label="module.rewardClaims.amountUsd" sortable={false} />
                <FunctionField
                  label="module.rewardClaims.token"
                  sortable={false}
                  render={(record: any) => {
                    const token = record?.token;
                    if (!token) return '-';
                    const shortened = `${token.slice(0, 6)}...${token.slice(-4)}`;
                    const chainId = Number(record?.chainId);
                    const explorerUrl =
                      chainId === 42161
                        ? `https://arbiscan.io/token/${token}`
                        : chainId === 501424
                          ? `https://solscan.io/token/${token}`
                          : null;
                    if (!explorerUrl) return <span>{shortened}</span>;
                    return (
                      <a href={explorerUrl} target="_blank" rel="noopener noreferrer">
                        {shortened}
                      </a>
                    );
                  }}
                />
                <FunctionField
                  label="module.rewardClaims.network"
                  sortable={false}
                  render={(record: any) => {
                    const map: Record<number, string> = { 42161: 'ARB', 501424: 'SOL' };
                    return map[Number(record?.chainId)] ?? record?.chainId ?? '-';
                  }}
                />
                <TextField source="type" label="module.rewardClaims.type" sortable={false} />
                <FunctionField
                  label="module.rewardClaims.result"
                  sortable={false}
                  render={(record: any) => {
                    const color = RESULT_COLOR[record?.result?.toLowerCase()] ?? 'default';
                    return <Chip label={record?.result ?? '-'} color={color} size="small" />;
                  }}
                />
                <FunctionField
                  label="module.rewardClaims.transactionHash"
                  sortable={false}
                  render={(record: any) => {
                    const hash = record?.transactionHash;
                    if (!hash) return '-';
                    const shortened = `${hash.slice(0, 8)}...${hash.slice(-6)}`;
                    const chainId = Number(record?.chainId);
                    const explorerUrl =
                      chainId === 42161
                        ? `https://arbiscan.io/tx/${hash}`
                        : chainId === 501424
                          ? `https://solscan.io/tx/${hash}`
                          : null;
                    if (!explorerUrl) return <span>{shortened}</span>;
                    return (
                      <a href={explorerUrl} target="_blank" rel="noopener noreferrer">
                        {shortened}
                      </a>
                    );
                  }}
                />
                <FunctionField
                  label="module.rewardClaims.errorMessage"
                  sortable={false}
                  render={(record: any) => {
                    const msg = record?.errorMessage;
                    if (!msg) return '-';
                    return (
                      <Tooltip title={msg} arrow placement="top">
                        <span style={{ display: 'block', width: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                          {msg}
                        </span>
                      </Tooltip>
                    );
                  }}
                />
                <TextField source="errorCode" label="module.rewardClaims.errorCode" sortable={false} />
                <FunctionField
                  label="module.rewardClaims.processedAt"
                  sortable={false}
                  render={(record: any) => {
                    if (!record?.processedAt) return '-';
                    return dayjs(record.processedAt).format('MMM D, YYYY HH:mm');
                  }}
                />
                <FunctionField
                  label="module.rewardClaims.actions"
                  sortable={false}
                  render={(record: any) => {
                    if (record?.result?.toLowerCase() !== 'failed') return null;
                    return (
                      <PermissionGuard resource={Resources.rewardClaims} action="update" fallback={<></>}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => setConfirmRecord({ id: record.id, transactionHash: record.transactionHash, chainId: Number(record.chainId) })}
                        >
                          Retry
                        </Button>
                      </PermissionGuard>
                    );
                  }}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog open={Boolean(confirmRecord)} onClose={() => setConfirmRecord(null)}>
          <DialogTitle>Confirm Retry</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to retry this claim? This action will re-submit the transaction.
            </DialogContentText>
            {confirmRecord?.transactionHash && (() => {
              const { transactionHash, chainId } = confirmRecord;
              const explorerUrl =
                chainId === 42161
                  ? `https://arbiscan.io/tx/${transactionHash}`
                  : chainId === 501424
                    ? `https://solscan.io/tx/${transactionHash}`
                    : null;
              if (!explorerUrl) return null;
              return (
                <DialogContentText sx={{ mt: 1 }}>
                  Check submitted transaction at{' '}
                  <a href={explorerUrl} target="_blank" rel="noopener noreferrer">
                    {`${transactionHash.slice(0, 8)}...${transactionHash.slice(-6)}`}
                  </a>
                </DialogContentText>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRecord(null)} disabled={retrying}>
              Cancel
            </Button>
            <Button onClick={handleRetry} color="warning" variant="contained" disabled={retrying}>
              {retrying ? 'Retrying...' : 'Retry'}
            </Button>
          </DialogActions>
        </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default RewardClaimsList;
